import { Card } from './card'
import { DateRangeShortcut } from '@blueprintjs/datetime'
import classNames from 'classnames'
import { format, isEqual, subDays } from 'date-fns'
import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { setHiddenColumns as setHiddenColumnsRedux } from '../../actions/user'
import { FilterI } from '../../constants/filter'
import { columnVisibility, VirtualizedTableWithoutBulkActionsProps } from '../../constants/table'
import { useDatesFromUrl } from '../../hooks/useDatesFromUrl'
import { useLocalStorage } from '../../hooks/useLocalStorage'
import { useOnProfileChange } from '../../hooks/useOnProfileChange'
import { useUser } from '../../hooks/useUser'
import useWindowDimensions from '../../hooks/useWindowDimensionsHook'
import { DefaultSelected } from '../../packages/blueprint-components/src/common'
import { Search } from '../../packages/blueprint-components/src/Search'
import { SavedFilter } from '../../packages/blueprint-components/src/useFilter'
import { useTableHooks } from '../../packages/blueprint-components/src/useTableHooks'
import { VirtualizedHTMLTable } from '../../packages/blueprint-components/src/VirtualizedHTMLTable'
import { axiosDestroy, axiosGet, axiosPatch } from '../../util/api'
import { isDefined } from '../../util/helpers'
import { isNavV2Enabled } from '../../util/nav-version'
import { Filter } from '../Filter'
import { Toasts } from '../toaster/Toast'
import { CardHeader } from './CardHeader'
import { DateRangeSelect, shortcuts } from './DateRangeSelect'
import { HideColumns } from './HideColumns'
import Profile = Models.Profile

// This feels a bit hacky, but it resolves an issue with a circular dependency between the
// VirtualizedTable, BulkActions, and OutOfSyncModal / AssAsinsModal components.

export const VirtualizedTableWithoutBulkActions = <T, Extra extends object>({
    load,
    tableHooks,
    filters,
    defaultFilter,
    search, // boolean - whether to show search bar
    searchPlaceholder,
    headerComponent,
    columns,
    name,
    modelName,
    reloadOnProfileChange = true,
    dateSelect, // boolean - whether to show date select
    showPerPage = true,
    perPage = 15,

    columnSelection = true, // boolean - whether to show column selection
    fetchData = {}, // array - if bulk actions has Dropdown modal send in the functions for fetching the dropdown data in array (ex. CampaignsTable)
    selectedValues,
    onSelectChange,
    selectKey = 'id',
    mutableFields,
    accordion,
    accordionRaw,
    hideIfNone = false,
    className = '',
    cardClassName,
    canSelect,
    canSelectFromAllPages = true,
    children,
    bulkActions,

    otherActionsLeft,
    otherActions,
    showHeader,
    enableRowSelect = true,
    height = null,
    rowHeight = 60,

    header,
    minDate = undefined,
    filterOverride = null,
    extra,
}: VirtualizedTableWithoutBulkActionsProps<T, Extra>): JSX.Element | null => {
    const windowDimensions = useWindowDimensions()
    const windowHeight = windowDimensions.height
    const { hiddenColumns, handleColumnSelect } = useFieldVisibility(name)
    const [entriesPerPage, setEntriesPerPage] = useState(perPage)
    const [customFilters, setCustomFilters] = useState<SavedFilter[]>([])
    const [selectedShortcutIndex, setSelectedShortcutIndex] = useState<number | undefined>(
        undefined,
    )
    const { user, activeProfile } = useUser()
    const localStorageHook = useLocalStorage('preferences', name, activeProfile ?? undefined)
    const updateQueryParams = useUpdateQueryParams()
    const datesFromUrl = useDatesFromUrl()
    const { pathname, search: locationSearch } = useLocation()
    const useNavV2 = useMemo(() => isNavV2Enabled(locationSearch), [locationSearch])
    const flattenForNavV2 = useNavV2 && pathname.startsWith('/admin')

    if (height == null) {
        height = windowHeight - 350
    }

    if (!tableHooks) {
        tableHooks = useTableHooks(load)
    }

    const visibleColumns = useMemo(
        () =>
            columnVisibility({
                allColumns: columns,
                hiddenColumns: hiddenColumns,
            }),
        [columns.length, columns, hiddenColumns],
    )

    const reload = async () => {
        tableHooks.setCommit(false)
        await applyProfilePreferencesFromLocalStorage()
        tableHooks.setCommit(true)
        // tableHooks.reload()
    }

    useOnProfileChange(() => (reloadOnProfileChange ? reload() : null))

    // Here we append any params that are needed before making the API call.
    // This guarantees that we won't be sending API calls until commit is true
    useEffect(() => {
        if (!!activeProfile) {
            if (!!filters) {
                fetchCustomFilters(name, null, setCustomFilters)
            }
            if (!!onSelectChange) {
                onSelectChange(DefaultSelected)
            }

            if (!tableHooks.commit) {
                if (!!defaultFilter || !!filterOverride) {
                    const additionalFilters: any = {}
                    if (!!defaultFilter) {
                        additionalFilters[defaultFilter?.filter?.key] = defaultFilter?.value
                    }
                    if (!!filterOverride) {
                        additionalFilters[filterOverride?.filter?.key] = filterOverride?.value
                    }

                    tableHooks.setParams((prev: any) => {
                        return {
                            ...prev,
                            filter: {
                                ...prev.filter,
                                ...additionalFilters,
                            },
                        }
                    })
                }

                loadProfilePreferencesFromLocalStorage().then(() => {
                    tableHooks.setCommit(true)
                })
            }
        }
    }, [activeProfile?.profile_id])

    const hasWidgets = filters || dateSelect || !!bulkActions || columnSelection || search

    const saveCustomFilter = (filterName: string, filters: FilterI[]) => {
        axiosPatch('custom-filters', {
            name: filterName,
            tableName: name.toLowerCase(),
            filters: filters ?? tableHooks.params.filter,
        })
            .then((_) => {
                Toasts.success(`${filterName} saved`)
                fetchCustomFilters(name, null, setCustomFilters)
            })
            .catch((err) => {
                Toasts.error(err.message)
            })
    }

    const deleteCustomFilter = (filterId: number) => {
        return axiosDestroy(`custom-filters/${filterId}`)
            .then((_) => {
                const filterName = customFilters.find(
                    (item: SavedFilter) => item.id == filterId,
                )?.name
                Toasts.success(`${filterName} deleted`)

                fetchCustomFilters(name, null, setCustomFilters)
            })
            .catch((err) => {
                Toasts.error(err.message)
            })
    }

    const editCustomFilter = (filterId: number, payload: FilterI[], overrideName: string) => {
        const currentName = customFilters.find((filter) => filter.id == filterId)?.name
        const filterName = overrideName ?? currentName
        return axiosPatch('custom-filters', {
            id: filterId,
            name: filterName,
            tableName: name.toLowerCase(),
            filters: payload,
        }).then(() => {
            Toasts.success(
                overrideName
                    ? `Filter renamed from ${currentName} to ${filterName}`
                    : `${filterName} edited successfully`,
            )
            fetchCustomFilters(name, null, setCustomFilters)
        })
    }

    const setPerPage = useCallback(
        (entries: number) => {
            persistPerPageToLocalStorage(entries)
            applyProfilePreferencesFromLocalStorage()
        },
        [entriesPerPage, activeProfile],
    )

    const queryByDate = ({ from, to }: { from: Date; to: Date }) => {
        const index = shortcuts.findIndex((i) => {
            return (
                isEqual(i.dateRange[0] ?? new Date(), from) &&
                isEqual(i.dateRange[1] ?? new Date(), to)
            )
        })

        setSelectedShortcutIndex(index === -1 ? undefined : index)
        tableHooks.setParams((prev: any) => ({
            ...prev,
            from,
            to,
        }))
    }

    const calculateBodyGridHeight = () => {
        const bottomPadding = 17
        const calculatedHeight = rowHeight * (tableHooks?.items ?? []).length + bottomPadding

        // Reserve space for pagination controls and table bottom section
        // The table bottom section includes pagination and per-page controls
        const hasBottomSection = tableHooks.pagination && tableHooks.pagination.total > 0
        const bottomSectionHeight = hasBottomSection ? 60 : 0
        const maxAllowedHeight = height - bottomSectionHeight

        if (calculatedHeight > maxAllowedHeight) {
            return maxAllowedHeight
        }

        return calculatedHeight
    }

    const tableMemo = useMemo(() => {
        return (
            <VirtualizedHTMLTable
                accordion={accordion}
                accordionRaw={accordionRaw}
                disableDefaultHtmlTableWrapperStyle={false}
                htmlTableWrapperClassname={'table-full-page-height'}
                className="mj-table"
                paginationClassname="mx-4 mb-4"
                columns={visibleColumns}
                allColumns={columns}
                {...tableHooks}
                selectedValues={selectedValues}
                onSelectChange={onSelectChange}
                selectKey={selectKey}
                canSelect={canSelect}
                canSelectFromAllPages={canSelectFromAllPages}
                showPerPage={showPerPage}
                perPage={entriesPerPage}
                setPerPage={(n: any) => setPerPage(Number(n))}
                stickyHeader
                stickyFooter={false}
                overscanRowCount={2}
                overscanColumnCount={20}
                enableRowSelect={enableRowSelect}
                height={calculateBodyGridHeight()}
                rowHeight={rowHeight}
                headerRowHeight={50}
                selectAllColumnWidth={120}
                hiddenColumns={hiddenColumns}
                setHiddenColumns={handleColumnSelect}
                extra={extra}
            />
        )
    }, [
        tableHooks.loading,
        tableHooks.items,
        selectedValues,
        height,
        accordion,
        accordionRaw,
        visibleColumns,
        columns,
        hiddenColumns,
        entriesPerPage,
        setPerPage,
    ])

    const persistDateRangeToLocalStorage = (shortcut: DateRangeShortcut, index: number) => {
        setSelectedShortcutIndex(index)
        localStorageHook.set({
            shortcut,
            index,
        })
    }

    const persistPerPageToLocalStorage = (perPage: number) => {
        localStorageHook.set({
            perPage,
        })
    }

    const applyProfilePreferencesFromLocalStorage = (): Promise<void> => {
        const savedProfilePreferences = localStorageHook.getForActiveProfile()
        if (!savedProfilePreferences) {
            return Promise.resolve()
        }

        let dateRangeShortcutIndex
        let perPage = 15
        const exists = Object(savedProfilePreferences).hasOwnProperty(name)
        if (exists) {
            const data = savedProfilePreferences[name]
            if (isDefined(data, 'index')) {
                dateRangeShortcutIndex = data.index
            }
            if (isDefined(data, 'perPage')) {
                perPage = data.perPage
            }
        }

        setEntriesPerPage(perPage)

        let from: Date = subDays(new Date(), 30)
        if (datesFromUrl.from) {
            from = datesFromUrl.from
        } else if (dateRangeShortcutIndex) {
            const d = shortcuts[dateRangeShortcutIndex]?.dateRange[0]
            if (d) {
                from = d
            }
        }

        let to: Date = new Date()
        if (datesFromUrl.to) {
            to = datesFromUrl.to
        } else if (dateRangeShortcutIndex) {
            const d = shortcuts[dateRangeShortcutIndex]?.dateRange[1]
            if (d) {
                to = d
            }
        }

        queryByDate({ from, to })
        return Promise.resolve()
    }

    /*
    We can shove everything that we want to make sure it's in place/loaded
    before the table starts pulling data. In other words, it prevents duplicate api calls.
    All of the functions that we want to make sure are executed
    must be return promise and we must `await` for them.
    */
    const loadProfilePreferencesFromLocalStorage = async () => {
        await applyProfilePreferencesFromLocalStorage()
        return Promise.resolve(true)
    }

    if (hideIfNone && tableHooks.items.length === 0) {
        return null
    }

    const FilterComponent = useMemo(() => {
        return (
            <Filter
                className="mr-2"
                filters={filters}
                disabled={tableHooks.loading}
                defaultFilter={defaultFilter}
                customFilters={customFilters}
                saveFilter={saveCustomFilter}
                deleteCustomFilter={deleteCustomFilter}
                editCustomFilter={editCustomFilter}
                onChange={(filters: any) => {
                    if (onSelectChange) {
                        onSelectChange(DefaultSelected)
                    }
                    tableHooks.setFilter(filters)
                }}
            />
        )
    }, [filters, tableHooks.loading, defaultFilter, customFilters])

    return (
        <div className={classNames(className)}>
            <Card
                className={classNames(
                    'd-flex flex-column p-0 bp4-card-rounded w-full',
                    flattenForNavV2 && 'flat-table-card !rounded-none !shadow-none',
                    cardClassName,
                )}
                style={{ boxShadow: flattenForNavV2 ? 'none' : undefined }}
            >
                {name && showHeader && <CardHeader title={name} component={headerComponent} />}
                {header}
                <div>
                    {children}
                    <div>
                        {hasWidgets && (
                            <div className="flex h-[49px] items-center justify-between">
                                <div className="flex flex-grow items-center">
                                    {dateSelect && (
                                        <div className="mx-2">
                                            <DateRangeSelect
                                                selectedShortcutIndex={selectedShortcutIndex}
                                                onShortcutChange={persistDateRangeToLocalStorage}
                                                minDate={
                                                    minDate
                                                        ? minDate
                                                        : user?.start_date
                                                          ? new Date(user.start_date)
                                                          : undefined
                                                }
                                                value={[
                                                    tableHooks.params?.from,
                                                    tableHooks.params?.to,
                                                ]}
                                                onChange={([from, to]) => {
                                                    if (from && to) {
                                                        updateQueryParams({
                                                            from: format(from, 'yyyy-MM-dd'),
                                                            to: format(to, 'yyyy-MM-dd'),
                                                        })

                                                        if (onSelectChange) {
                                                            onSelectChange(DefaultSelected)
                                                        }
                                                        queryByDate({ from, to })
                                                    }
                                                }}
                                            />
                                        </div>
                                    )}
                                    <div className="flex items-center">
                                        {filters ? FilterComponent : <span />}
                                    </div>
                                    {otherActionsLeft}
                                </div>
                                <div className="flex items-center">
                                    {search && (
                                        <Search
                                            className="w-full mr-2"
                                            width={290}
                                            style={{ width: '290px' }}
                                            loading={tableHooks.loading}
                                            placeholder={searchPlaceholder}
                                            onSearch={(query: string) => {
                                                if (onSelectChange) {
                                                    onSelectChange(DefaultSelected)
                                                }
                                                tableHooks.setQuery(query)
                                            }}
                                        />
                                    )}
                                    {otherActions}
                                    {!!bulkActions &&
                                        bulkActions({
                                            columns,
                                            selectKey,
                                            fetchData,
                                            params: tableHooks.params,
                                            reload: (clearSelection: any) => {
                                                tableHooks.reload()
                                                if (clearSelection) {
                                                    onSelectChange(DefaultSelected)
                                                }
                                            },
                                            name,
                                            modelName,
                                            disabled: !(
                                                selectedValues &&
                                                (selectedValues?.safeList.length > 0 ||
                                                    selectedValues?.blockList.length > 0 ||
                                                    selectedValues?.all) &&
                                                mutableFields
                                            ),
                                            mutableFields,
                                            selectedValues,
                                            setSelectedValues: onSelectChange,
                                            filterOverride,
                                        })}
                                    {columnSelection && (
                                        <HideColumns
                                            className="mx-2 rounded-lg"
                                            columns={columns}
                                            hiddenColumns={hiddenColumns}
                                            setHiddenColumns={handleColumnSelect}
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <div>{tableMemo}</div>
                </div>
            </Card>
        </div>
    )
}

const getHiddenColumns = (activeProfile: Profile | undefined, name: string) => {
    const fv = activeProfile?.field_visibility

    if (!fv) {
        return []
    }

    const field_visibility = fv.find((fv) => fv.table_name?.toLowerCase() === name?.toLowerCase())

    return field_visibility?.hidden_columns || []
}

export const useFieldVisibility = (name: string) => {
    const dispatch = useDispatch()
    const { activeProfile } = useUser()
    const [count, setCount] = useState(0)
    const hiddenColumns = useMemo(() => {
        return getHiddenColumns(activeProfile ?? undefined, name)
    }, [activeProfile, name, count])

    const updateHiddenColumns = ({ newColumns }: { newColumns: string[] }) => {
        dispatch(setHiddenColumnsRedux(name, newColumns))
        setCount((prev) => prev + 1)

        axiosPatch('field-visibility', {
            table_name: name,
            hidden_columns: newColumns,
        })
    }

    const handleColumnSelect = (columnName: string) => {
        let newCols
        if (!hiddenColumns.includes(columnName)) {
            newCols = [...hiddenColumns, columnName]
        } else {
            newCols = hiddenColumns.filter((item) => item != columnName)
        }

        updateHiddenColumns({ newColumns: newCols })
    }

    return {
        hiddenColumns,
        handleColumnSelect,
    }
}

export const fetchCustomFilters = (
    name: string,
    overrideName: string | null,
    setCustomFilters: Dispatch<SetStateAction<SavedFilter[]>>,
) => {
    if (!name) {
        console.error('Cannot fetch custom filters if name prop not set')
        return
    }

    axiosGet('custom-filters', {
        tableName: overrideName ?? name.toLowerCase(),
    }).then((res) => {
        setCustomFilters(res.data)
    })
}

const useUpdateQueryParams = () => {
    const navigate = useNavigate()
    const location = useLocation()

    return (updatedParams: { [key: string]: string }) => {
        // Create a URLSearchParams object from the current query string
        const searchParams = new URLSearchParams(location.search)

        // Update the parameters
        for (const [key, value] of Object.entries(updatedParams)) {
            searchParams.set(key, value)
        }

        // Navigate to the same path with new search parameters
        // @ts-ignore
        navigate({
            pathname: location.pathname,
            search: '?' + searchParams.toString(),
            replace: true,
        })
    }
}
